/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState } from "react";
import api from "../../../axiosConfig";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../UserContext";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  Typography,
  useMediaQuery,
  CircularProgress,
  Fade,
  Paper,
  keyframes,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import {
  AccountCircle,
  Lock,
  Visibility,
  VisibilityOff,
  DirectionsCar,
  LocationOn,
} from "@mui/icons-material";
import logo from "../../../Assets/img/logo.webp";
import { esAdministrador } from "../../../utils/perfil";

// Animaciones
const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-10px);
  }
`;

const scanAnimation = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

// Contenedor principal con fondo degradado
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #0a2e5c 0%, #051b38 100%)',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2),
  backgroundImage: `
    radial-gradient(circle at 20% 80%, rgba(10, 46, 92, 0.4) 0%, transparent 60%),
    radial-gradient(circle at 80% 20%, rgba(10, 46, 92, 0.25) 0%, transparent 60%),
    radial-gradient(circle at 60% 60%, rgba(10, 46, 92, 0.18) 0%, transparent 50%)
  `,
}));

// Logo con efectos
const StyledLogo = styled('img')(({ theme, isMobile }) => ({
  width: isMobile ? 200 : 280,
  marginBottom: theme.spacing(4),
  filter: 'drop-shadow(0 0 20px rgba(10, 46, 92, 0.8))',
  animation: `${floatAnimation} 6s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    width: 180,
    marginBottom: theme.spacing(3),
  },
}));

// Contenedor del formulario con glass effect
const FormContainer = styled(Paper)(({ theme, isMobile }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  padding: isMobile ? theme.spacing(3) : theme.spacing(5),
  borderRadius: 20,
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    0 0 30px rgba(10, 46, 92, 0.2)
  `,
  position: 'relative',
  overflow: 'hidden',
  maxWidth: isMobile ? '90vw' : 400,
  width: '100%',
  
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.1), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
}));

// Input personalizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 10,
    
    '& fieldset': {
      borderColor: 'rgba(10, 46, 92, 0.3)',
      borderWidth: 2,
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(10, 46, 92, 0.5)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: '#0a2e5c',
      boxShadow: '0 0 20px rgba(10, 46, 92, 0.4)',
    },
    
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: '#2c3e50',
    fontWeight: 600,
    zIndex: 15,
    
    '&.Mui-focused': {
      color: '#0a2e5c',
      fontWeight: 700,
    },
    
    '&.MuiFormLabel-filled': {
      color: '#2c3e50',
    },
  },
  
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#0a2e5c',
    fontSize: '1.4rem',
    filter: 'drop-shadow(0 0 3px rgba(10, 46, 92, 0.5))',
    transition: 'all 0.3s ease',
  },
  
  '& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
    transform: 'scale(1.1)',
    filter: 'drop-shadow(0 0 5px rgba(10, 46, 92, 0.6))',
  },
}));

// Botón principal
const LoginButton = styled(Button)(({ theme, isMobile }) => ({
  width: '100%',
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  marginTop: theme.spacing(3),
  borderRadius: 12,
  background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
  color: 'white',
  fontSize: isMobile ? '0.9rem' : '1rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 20px rgba(10, 46, 92, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  zIndex: 10,
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s ease',
  },
  
  '&:hover::before': {
    left: '100%',
  },
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 25px rgba(10, 46, 92, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    background: 'linear-gradient(135deg, #051b38, #082340)',
  },
  
  '&:active': {
    transform: 'translateY(0)',
  },
  
  '&:disabled': {
    background: 'rgba(10, 46, 92, 0.5)',
    transform: 'none',
    '&::before': {
      display: 'none',
    },
  },
}));

// Botón secundario para recuperar contraseña
const SecondaryButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  borderRadius: 12,
  border: '2px solid rgba(10, 46, 92, 0.3)',
  color: '#0a2e5c',
  fontSize: '0.9rem',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 10,
  
  '&:hover': {
    backgroundColor: 'rgba(10, 46, 92, 0.05)',
    borderColor: '#0a2e5c',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px rgba(10, 46, 92, 0.2)',
  },
}));

// Título con iconos
const WelcomeTitle = styled(Typography)(({ theme, isMobile }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  color: '#0a2e5c',
  fontSize: isMobile ? '1.5rem' : '1.8rem',
  fontWeight: 700,
  position: 'relative',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));


function LoginForm() {
  // Estados
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  // La misma cédula es conductor y administrador de vehículos: elige con qué perfil entra
  const [perfilesDisponibles, setPerfilesDisponibles] = useState(null);

  // Hooks
  const navigate = useNavigate();
  const { loginUser } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Función para manejar el inicio de sesión (perfil: el elegido cuando la persona tiene dos)
  const handleLogin = async (e, perfil = null) => {
    if (e) e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    try {
      const response = await api.post("/auth/app/login", {
        identifier,
        password,
        ...(perfil ? { perfil } : {}),
      });

      if (response.data.seleccionar_perfil) {
        setPerfilesDisponibles(response.data.perfiles || []);
        return;
      }

      const {
        accessToken,
        refreshToken,
        user,
        requiere_cambio_password,
        perfiles,
      } = response.data;

      // Almacenar los tokens y usuario en UserContext (esperar a que termine)
      // Agregar el flag y los perfiles (cambio de perfil desde el menú) al usuario
      const userWithFlag = { ...user, requiere_cambio_password, perfiles: perfiles || [] };
      await loginUser(userWithFlag, accessToken, refreshToken);
      setPerfilesDisponibles(null);

      // Si requiere cambio de contraseña, redirigir a la pantalla de cambio
      if (requiere_cambio_password) {
        navigate("/change-password?mandatory=true");
        return;
      }

      // Administrador de vehículos: directo a los traslados de sus vehículos (solo lectura)
      if (esAdministrador(user)) {
        navigate("/modules/Dashboard");
        return;
      }

      // Verificar si el conductor tiene vehículo asignado
      const vehiculoPrincipal = localStorage.getItem("vehiculoPrincipal");
      const tieneVehiculo = vehiculoPrincipal && vehiculoPrincipal !== "null" && vehiculoPrincipal !== "undefined";

      if (tieneVehiculo) {
        // Con vehículo: ir al formulario preoperacional (obligatorio)
        navigate("/modules/PreOperationalForm");
      } else {
        // Sin vehículo: ir directo al Dashboard (puede ver traslados históricos)
        navigate("/modules/Dashboard");
      }

    } catch (error) {
      console.error("Error al iniciar sesión:", error);

      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.request) {
        setErrorMessage("No se recibió respuesta del servidor.");
      } else {
        setErrorMessage("Error al iniciar sesión.");
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <LoginContainer>
      <StyledLogo 
        src={logo} 
        alt="Mogotax Logo" 
        isMobile={isMobile}
      />
      
      <FormContainer elevation={0} isMobile={isMobile}>
        <WelcomeTitle variant="h4" isMobile={isMobile}>
          <DirectionsCar sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
          Bienvenido
          <LocationOn sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
        </WelcomeTitle>
        

        {perfilesDisponibles ? (
          <Box sx={{ position: 'relative', zIndex: 10 }}>
            <Typography sx={{ textAlign: 'center', color: '#2c3e50', fontWeight: 600, mb: 1 }}>
              ¿Con qué perfil deseas ingresar?
            </Typography>
            <Typography sx={{ textAlign: 'center', color: '#5f6b7a', fontSize: '0.85rem', mb: 1 }}>
              Podrás cambiar de perfil después desde el menú lateral.
            </Typography>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
                {errorMessage}
              </Alert>
            )}
            {perfilesDisponibles.map((p) => (
              <LoginButton
                key={p.perfil}
                variant="contained"
                disabled={loading}
                isMobile={isMobile}
                onClick={() => handleLogin(null, p.perfil)}
              >
                {p.etiqueta}
              </LoginButton>
            ))}
            <SecondaryButton
              variant="outlined"
              disabled={loading}
              onClick={() => {
                setPerfilesDisponibles(null);
                setErrorMessage("");
              }}
            >
              Volver
            </SecondaryButton>
          </Box>
        ) : (
        <Box component="form" onSubmit={handleLogin} sx={{ position: 'relative', zIndex: 10 }}>
          <StyledTextField
            fullWidth
            label="Email o Número de Documento"
            variant="outlined"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Contraseña"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    size="small"
                    sx={{
                      color: '#0a2e5c',
                      '&:hover': {
                        backgroundColor: 'rgba(10, 46, 92, 0.1)',
                      },
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {errorMessage && (
            <Fade in={Boolean(errorMessage)}>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                {errorMessage}
              </Alert>
            </Fade>
          )}

          <LoginButton
            type="submit"
            variant="contained"
            disabled={loading}
            isMobile={isMobile}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Iniciando sesión...
              </Box>
            ) : (
              "Iniciar Sesión"
            )}
          </LoginButton>

          <SecondaryButton
            onClick={handleForgotPassword}
            variant="outlined"
          >
            ¿Olvidaste tu contraseña?
          </SecondaryButton>
        </Box>
        )}
      </FormContainer>
    </LoginContainer>
  );
}

export default LoginForm;